/**
 * Estrutura Funcional (ADR 0022): tabelas corporativas de classificação compartilhadas pelos
 * módulos. Validações das telas e regras puras da importação inicial.
 */
import { z } from 'zod';
import { localizedText, uuid } from './schemas.ts';

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

/** Código do NetSuite: só a referência (texto livre curto), nunca a chave do registro. */
export const netsuiteCode = optionalText(100);

export const classificationCategoryInput = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_-]{1,39}$/, 'Use letras maiúsculas, números, hífen ou _'),
  name: localizedText,
  description: z
    .object({ 'pt-BR': z.string().trim().max(500), en: z.string().trim().max(500) })
    .default({ 'pt-BR': '', en: '' }),
  hasElimination: z.boolean().default(false),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(1).max(999).default(100),
});
export type ClassificationCategoryInput = z.infer<typeof classificationCategoryInput>;

/** Escopo da regra de Tipos de Gasto de um Tipo de Classificação (ADR 0048). */
export const EXPENSE_TYPE_SCOPES = ['all', 'selected'] as const;
export type ExpenseTypeScope = (typeof EXPENSE_TYPE_SCOPES)[number];

/** Todos os Tipos de Gasto, ou só os selecionados (ao menos um). */
export const expenseTypeRule = z
  .object({
    scope: z.enum(EXPENSE_TYPE_SCOPES),
    ids: z.array(uuid).max(100).default([]),
  })
  .refine((v) => v.scope === 'all' || v.ids.length > 0, {
    message: 'Escolha ao menos um Tipo de Gasto, ou marque "Todos os tipos de gasto"',
    path: ['ids'],
  })
  .transform((v) => ({ scope: v.scope, ids: v.scope === 'all' ? [] : [...new Set(v.ids)] }));
export type ExpenseTypeRule = z.infer<typeof expenseTypeRule>;

/**
 * O par (Tipo de Classificação, Tipo de Gasto) é permitido? A mesma regra do banco
 * (core.expense_type_allowed), para as telas e importações avisarem antes de gravar. Campo vazio
 * de qualquer lado não é conferido aqui.
 */
export function expenseTypeAllowed(
  rule: { scope: ExpenseTypeScope; ids: readonly string[] } | null | undefined,
  expenseTypeId: string | null | undefined,
): boolean {
  if (!rule || !expenseTypeId) return true;
  return rule.scope === 'all' || rule.ids.includes(expenseTypeId);
}

export const classificationInput = z.object({
  /** Código interno ou gerencial (opcional). */
  code: optionalText(40).refine(
    (v) => v == null || /^[A-Za-z0-9][A-Za-z0-9._/-]{0,39}$/.test(v),
    'Use letras, números, ponto, barra, hífen ou _',
  ),
  name: z.string().trim().min(1).max(200),
  description: optionalText(1000),
  legalEntityId: uuid.nullable().optional(),
  departmentId: uuid.nullable().optional(),
  isElimination: z.boolean().default(false),
  /** Classificação superior (subcategoria). Ausente = não muda; nulo = sem superior. */
  parentId: uuid.nullable().optional(),
  netsuiteCode,
  /**
   * Só nos Tipos de Classificação (ADR 0048): com quais Tipos de Gasto o registro pode ser usado.
   * Ausente = não muda.
   */
  expenseTypes: expenseTypeRule.optional(),
});
export type ClassificationInput = z.infer<typeof classificationInput>;

export const structureStatusInput = z.object({ isActive: z.boolean() });

export const subsidiaryInput = z.object({
  netsuiteCode,
  isElimination: z.boolean(),
});

// ----- Plano de contas gerencial e unidades de negócio (ADR 0032) -----

/** Natureza da conta gerencial. */
export const ACCOUNT_NATURES = [
  'revenue',
  'revenue_deduction',
  'cost',
  'operating_expense',
  'other_operating',
  'financial',
  'income_tax',
  'capex',
  'statistical',
] as const;
export type AccountNature = (typeof ACCOUNT_NATURES)[number];

export const managementAccountInput = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,39}$/, 'Use letras, números, ponto, hífen ou _'),
  name: localizedText,
  description: optionalText(1000),
  parentId: uuid.nullable().default(null),
  isSynthetic: z.boolean(),
  nature: z.enum(ACCOUNT_NATURES),
  sortOrder: z.number().int().min(1).max(99999).default(100),
});
export type ManagementAccountInput = z.infer<typeof managementAccountInput>;

export const businessUnitInput = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_-]{1,29}$/, 'Use de 2 a 30 letras maiúsculas, números, - ou _'),
  name: z.string().trim().min(1).max(200),
  description: optionalText(1000),
  legalEntityId: uuid.nullable().default(null),
  managerUserId: uuid.nullable().default(null),
});
export type BusinessUnitInput = z.infer<typeof businessUnitInput>;

/**
 * Árvore do plano de contas em ordem de apresentação (pai antes dos filhos, irmãos pela ordem e
 * pelo código), com o nível de cada conta. Contas cujo superior não está na lista viram raízes.
 */
export function accountTree<
  T extends { id: string; parentId: string | null; sortOrder: number; code: string },
>(accounts: readonly T[]): (T & { level: number })[] {
  const ids = new Set(accounts.map((a) => a.id));
  const children = new Map<string | null, T[]>();
  for (const a of accounts) {
    const key = a.parentId && ids.has(a.parentId) ? a.parentId : null;
    children.set(key, [...(children.get(key) ?? []), a]);
  }
  const out: (T & { level: number })[] = [];
  const walk = (parent: string | null, level: number) => {
    const list = [...(children.get(parent) ?? [])].sort(
      (x, y) => x.sortOrder - y.sortOrder || x.code.localeCompare(y.code),
    );
    for (const a of list) {
      out.push({ ...a, level });
      walk(a.id, level + 1);
    }
  };
  walk(null, 0);
  return out;
}

export const STRUCTURE_RECORD_TYPES = [
  'department',
  'cost_center',
  'legal_entity',
  'classification',
  'management_account',
  'business_unit',
] as const;
export type StructureRecordType = (typeof STRUCTURE_RECORD_TYPES)[number];

export const structureRecordQuery = z.object({
  type: z.enum(STRUCTURE_RECORD_TYPES),
  id: uuid,
});

// ----- Regras da importação inicial (planilha "Códigos de classificações") -----

/** Nome comparável: sem acentos, pontuação e espaços repetidos, em maiúsculas. */
export function normalizeName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
}

/** Código gerado a partir do nome (ex.: "CREATION AND ART" → "CREATION-AND-ART"), até 30 letras. */
export function codeFromName(name: string, max = 30): string {
  const code = normalizeName(name).replace(/ /g, '-').slice(0, max).replace(/-+$/, '');
  return code.length >= 2 ? code : `X-${code}`.slice(0, max);
}

/**
 * Centro de custo da planilha: "010211002 - ART PRODUCTION MANAGEMENT" → código de 9 números,
 * nome e o prefixo de 2 números (que indica a subsidiária). Sem o padrão, devolve só o nome.
 */
export function parseCostCenterName(raw: string): {
  code: string | null;
  name: string;
  subsidiaryPrefix: number | null;
} {
  const m = /^(\d{9})\s*-\s*(.+)$/.exec(raw.trim());
  if (!m) return { code: null, name: raw.trim(), subsidiaryPrefix: null };
  return { code: m[1]!, name: m[2]!.trim(), subsidiaryPrefix: Number(m[1]!.slice(0, 2)) };
}

/** "Sim"/"Não" da planilha. */
export function yes(value: string | null | undefined): boolean {
  return normalizeName(value ?? '') === 'SIM';
}

// ----- Importação pela tela -----

/** Planilha enviada pelo navegador (em base64; não é guardada). Até ~500 KB. */
export const structureImportFile = z.object({
  fileName: z.string().trim().min(1).max(200),
  contentBase64: z.string().min(1).max(700_000),
});

/** Tabelas da planilha (abas do modelo). */
export const STRUCTURE_IMPORT_TABLES = [
  'departments',
  'expenseTypes',
  'costCenters',
  'subsidiaries',
  'classifications',
] as const;

/** Modelo de uma tabela só; sem `table`, o modelo completo. */
export const structureTemplateQuery = z.object({
  table: z.enum(STRUCTURE_IMPORT_TABLES).optional(),
});

/** Gravar exige a simulação do mesmo arquivo (SHA-256) e a confirmação expressa. */
export const structureImportApply = structureImportFile.extend({
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  confirm: z.literal(true),
});

// ----- Pendências da importação -----

const resolutionNote = z.string().trim().min(1).max(1000);

/**
 * Regularizar uma pendência de centro de custo: criar o centro (com a empresa e o código
 * definidos), ligar o ID NetSuite a um centro existente (como atual ou como código anterior) ou
 * encerrar com uma observação.
 */
export const importPendingResolveInput = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('create_cost_center'),
    legalEntityId: uuid,
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z0-9][A-Z0-9._-]{0,39}$/, 'Use letras, números, ponto, hífen ou _'),
    name: z.string().trim().min(1).max(200),
    isActive: z.boolean().default(true),
    note: resolutionNote.optional(),
  }),
  z.object({
    action: z.literal('link_cost_center'),
    costCenterId: uuid,
    asHistory: z.boolean().default(false),
    note: resolutionNote.optional(),
  }),
  z.object({ action: z.literal('close'), note: resolutionNote }),
]);
export type ImportPendingResolveInput = z.infer<typeof importPendingResolveInput>;
